/**
 * useVisibleNodeIds Hook
 *
 * Pure filtering logic for computing which nodes should be visible
 * based on selected label filters. Includes ancestor traversal to
 * keep parent nodes visible when children match.
 *
 * This is the core filtering algorithm shared across:
 * - MindMap canvas (via useNodeLabelFiltering)
 * - ReportSidebar
 * - ReportPage
 */

import { useMemo, useCallback } from 'react'
import type { VisibleNodeIdsConfig, VisibleNodeIdsResult } from '../types'

/**
 * Hook for computing visible node IDs based on label filter.
 *
 * When labels are selected:
 * 1. Finds all nodes that have at least one of the selected labels
 * 2. Includes all ancestor nodes (parents up to root) to maintain hierarchy
 * 3. Returns a Set for O(1) visibility checks
 *
 * @example
 * ```tsx
 * const { visibleNodeIds, shouldDisplayNode } = useVisibleNodeIds({
 *   nodeLabelMap,
 *   parentMap,
 *   allNodeIds: nodes.map(n => n.id),
 *   selectedFilterLabels,
 * })
 *
 * // Filter nodes
 * const filteredNodes = nodes.filter(n => shouldDisplayNode(n.id))
 * ```
 */
export function useVisibleNodeIds({
  nodeLabelMap,
  parentMap,
  allNodeIds,
  selectedFilterLabels,
}: VisibleNodeIdsConfig): VisibleNodeIdsResult {
  const visibleNodeIds = useMemo(() => {
    // No filter active - all nodes visible
    if (selectedFilterLabels.length === 0) {
      return new Set(allNodeIds)
    }

    const visible = new Set<string>()

    // Find nodes with selected labels and their ancestors
    allNodeIds.forEach((nodeId) => {
      const nodeLabels = nodeLabelMap.get(nodeId) ?? []
      const hasSelectedLabel = selectedFilterLabels.some((labelId) => nodeLabels.includes(labelId))

      if (hasSelectedLabel) {
        visible.add(nodeId)

        // Walk up the tree and add all ancestors (with cycle detection)
        let currentId = nodeId
        const visited = new Set<string>([currentId])
        let parentId = parentMap.get(currentId)

        while (parentId !== undefined) {
          // Cycle detection - stop if we've seen this parent before
          if (visited.has(parentId)) {
            break
          }
          visited.add(parentId)
          visible.add(parentId)
          currentId = parentId
          parentId = parentMap.get(currentId)
        }
      }
    })

    return visible
  }, [allNodeIds, nodeLabelMap, selectedFilterLabels, parentMap])

  const shouldDisplayNode = useCallback(
    (nodeId: string): boolean => {
      return visibleNodeIds.has(nodeId)
    },
    [visibleNodeIds]
  )

  const hasActiveFilter = selectedFilterLabels.length > 0

  return {
    visibleNodeIds,
    shouldDisplayNode,
    hasActiveFilter,
  }
}

/**
 * Build a parent map from edges for use with useVisibleNodeIds.
 * Maps each node to its parent (child -> parent).
 */
export function buildParentMap(
  edges: Array<{ source: string; target: string }>
): Map<string, string> {
  const map = new Map<string, string>()
  edges.forEach((edge) => {
    map.set(edge.target, edge.source)
  })
  return map
}

/**
 * Build a node label map from nodes for use with useVisibleNodeIds.
 * Maps each node ID to its label IDs.
 */
export function buildNodeLabelMap(
  nodeIds: string[],
  getNodeLabels: (nodeId: string) => Array<{ id: string }>
): Map<string, string[]> {
  const map = new Map<string, string[]>()
  nodeIds.forEach((nodeId) => {
    const labels = getNodeLabels(nodeId)
    if (labels.length > 0) {
      map.set(
        nodeId,
        labels.map((l) => l.id)
      )
    }
  })
  return map
}
